import handleTeamAssistV2Error from 'actions/team_assist_v2/lib/handle_team_assist_v2_error';
import isTeamAssistV2Enabled from 'actions/team_assist_v2/lib/is_team_assist_v2_enabled';
import TeamAssistV2Turn from 'models/team_assist_v2_turn';

// glados reads the existing feedback before it upserts, so two requests for the
// same answer in flight at once can commit out of order. Each waits for the last.
const inFlightByMessageId = new Map();
// The rating glados last recorded for each answer with a send still out. The
// store holds the agent's latest click, so a failure rolls back to this.
const confirmedByMessageId = new Map();

/**
 * Sets the agent's rating on a turn's answer (`{ value, reason?, comment? }`),
 * or clears it with null. The thumbs change at once; if glados doesn't record
 * the rating, they go back to what they were and the failure is reported.
 */
export default class RateTeamAssistV2Answer {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, turnId, rating }) {
    if (!isTeamAssistV2Enabled(this.context.stores.appFeatures.get())) {
      return;
    }
    const store = this.storeFor(customerId);
    if (!store || !store.has({ id: turnId })) {
      return;
    }
    const turn = store.find(turnId);
    const messageId = turn.answer && turn.answer.messageId;
    if (!messageId) {
      return;
    }

    const next = rating || null;
    if (!confirmedByMessageId.has(messageId)) {
      confirmedByMessageId.set(messageId, turn.answer.rating || null);
    }
    setRating(store, turnId, next);

    const sent = sendInOrder(this.context.gateways.teamAssistV2, messageId, next);
    return sent
      .then(
        () => confirmedByMessageId.set(messageId, next),
        err => this.onRateFailed({ customerId, turnId, messageId, next, err })
      )
      .finally(() => forgetIfLast(messageId, sent));
  }

  onRateFailed({ customerId, turnId, messageId, next, err }) {
    const current = this.storeFor(customerId);
    // A later rating owns the thumbs now; don't undo it.
    if (current && current.has({ id: turnId }) && sameRating(current.find(turnId).answer.rating, next)) {
      setRating(current, turnId, confirmedByMessageId.get(messageId));
    }
    handleTeamAssistV2Error(this.context, err, { isInteractive: true });
  }

  storeFor(customerId) {
    if (!customerId || !this.context.stores.customers.has({ id: customerId })) {
      return null;
    }
    return this.context.stores.customers.storesFor(customerId).teamAssistsV2;
  }
}

function sendInOrder(gateway, messageId, rating) {
  const send = () => gateway.rate({ messageId, rating });
  const inFlight = inFlightByMessageId.get(messageId);
  const sent = inFlight ? inFlight.then(send, send) : send();
  inFlightByMessageId.set(messageId, sent);
  return sent;
}

function forgetIfLast(messageId, sent) {
  if (inFlightByMessageId.get(messageId) === sent) {
    inFlightByMessageId.delete(messageId);
    confirmedByMessageId.delete(messageId);
  }
}

function setRating(store, turnId, rating) {
  const turn = store.find(turnId);
  store.addOrReplace(TeamAssistV2Turn.create({ ...turn.toJs(), answer: { ...turn.answer, rating } }));
}

function sameRating(a, b) {
  return JSON.stringify(a || null) === JSON.stringify(b || null);
}
